import { useEffect, useRef } from 'react'
import { useTheme } from '@/context/themeContext'
import { subscribeEnergy } from '@/lib/energyBus'

// The dark palette reads muddy on a white page, so light mode gets
// brighter, more saturated stops and a stronger base opacity.
const PALETTES = {
  dark: {
    blob1: 'linear-gradient(to right, rgb(73, 54, 245), rgb(39, 151, 221))',
    blob2: 'linear-gradient(to right, rgb(23, 76, 222), rgb(245, 54, 156))',
    blob3: 'linear-gradient(to right, rgb(54, 38, 194), rgb(120, 9, 142))',
    opacityBoost: 1,
  },
  light: {
    blob1: 'linear-gradient(to right, rgb(122, 104, 255), rgb(64, 196, 255))',
    blob2: 'linear-gradient(to right, rgb(96, 140, 255), rgb(255, 94, 190))',
    blob3: 'linear-gradient(to right, rgb(148, 92, 255), rgb(224, 84, 255))',
    opacityBoost: 1.6,
  },
}

export function GradientAnimation({
  animationDuration,
  scoped = false,
  opacity = 0.3,
  reactive = false,
}: {
  animationDuration: string
  /** Render inside a `relative overflow-hidden` parent instead of covering the viewport. */
  scoped?: boolean
  opacity?: number
  /** Subscribe to the energy bus (typing / future voice amplitude) and flare with it. */
  reactive?: boolean
}) {
  const { theme } = useTheme()
  const isDark =
    theme === 'dark' ||
    (theme === 'system' &&
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches)
  const palette = isDark ? PALETTES.dark : PALETTES.light
  const baseOpacity = Math.min(0.5, opacity * palette.opacityBoost)

  const layerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!reactive) return
    const layer = layerRef.current
    if (!layer) return
    // Direct style mutation per frame — no React re-renders while energy decays.
    return subscribeEnergy((level) => {
      layer.style.opacity = String(Math.min(0.85, baseOpacity * (1 + level * 1.4)))
      layer.style.filter = `blur(100px) saturate(${1 + level * 0.9})`
      layer.style.transform = `scale(${1 + level * 0.06})`
    })
  }, [reactive, baseOpacity])

  return (
    <div
      className={
        scoped
          ? 'pointer-events-none absolute inset-0 z-0 overflow-hidden'
          : 'fixed inset-0 z-[-1] overflow-hidden'
      }>
      <div
        ref={layerRef}
        className="absolute bottom-0 left-0 right-0 top-0 blur-[100px]"
        style={{ opacity: baseOpacity }}>
        {/* Blob 1 - Primary to Secondary */}
        <div
          className="absolute left-1/2 top-1/2 h-[400px] w-[700px] rounded-full"
          style={{
            background: palette.blob1,
            animation: `blob1 ${animationDuration} infinite`,
          }}
        />

        {/* Blob 2 - Secondary to Primary */}
        <div
          className="absolute left-1/2 top-1/2 h-[500px] w-[500px] rounded-full"
          style={{
            background: palette.blob2,
            animation: `blob2 ${animationDuration} infinite`,
          }}
        />

        {/* Blob 3 - Primary to Secondary with varied transparency */}
        <div
          className="absolute left-1/2 top-1/2 h-[600px] w-[400px] rounded-full"
          style={{
            background: palette.blob3,
            animation: `blob3 ${animationDuration} infinite`,
          }}
        />
      </div>
    </div>
  )
}
